import {
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState
} from 'react';

import { Form } from 'react-bootstrap';
import { useController } from 'react-hook-form';

const defaultLength = 6;

const sanitizeCode = (value: string, length: number) => value.replace(/\D/g, '').slice(0, length);

const toSlots = (value: string, length: number) =>
  Array.from({ length }, (_, index) => sanitizeCode(value, length)[index] ?? '');

export interface VerificationCodeFieldProps {
  name: string;
  label: string;
  digitAriaLabel: (position: number, total: number) => string;
  length?: number;
}

export function VerificationCodeField({
  name,
  label,
  digitAriaLabel,
  length = defaultLength
}: VerificationCodeFieldProps) {
  const { field, fieldState } = useController({ name });
  const [digits, setDigits] = useState(() => toSlots(String(field.value ?? ''), length));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const reportedValue = useRef<string | undefined>(undefined);
  const errorId = `${name}-error`;

  useEffect(() => {
    const externalValue = sanitizeCode(String(field.value ?? ''), length);

    if (externalValue !== reportedValue.current) {
      setDigits(toSlots(externalValue, length));
      reportedValue.current = externalValue;
    }
  }, [field.value, length]);

  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus();
  };

  const updateDigits = (index: number, value: string) => {
    const insertedDigits = sanitizeCode(value, length);
    const nextDigits = [...digits];

    insertedDigits
      .slice(0, length - index)
      .split('')
      .forEach((digit, offset) => {
        nextDigits[index + offset] = digit;
      });

    if (!insertedDigits) {
      nextDigits[index] = '';
    }

    const nextValue = nextDigits.join('');
    setDigits(nextDigits);
    reportedValue.current = nextValue;
    field.onChange(nextValue);

    if (insertedDigits) {
      focusInput(Math.min(index + insertedDigits.length, length - 1));
    }
  };

  const handleChange = (
    index: number,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    updateDigits(index, event.target.value);
  };

  const handlePaste = (
    index: number,
    event: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.preventDefault();
    updateDigits(index, event.clipboardData.getData('text'));
  };

  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event.key === 'Backspace') {
      event.preventDefault();

      if (digits[index]) {
        updateDigits(index, '');
      } else if (index > 0) {
        focusInput(index - 1);
      }

      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
    }

    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      focusInput(index + 1);
    }
  };

  return (
    <Form.Group as="fieldset" className="border-0 m-0 p-0">
      <Form.Label as="legend" className="mb-2">
        {label}
      </Form.Label>
      <div className="d-flex gap-2" role="group" aria-label={label}>
        {digits.map((digit, index) => (
          <div className={index === Math.ceil(length / 2) ? 'ms-1 ms-sm-2' : undefined} key={index}>
            <Form.Control
              ref={(element) => {
                inputRefs.current[index] = element;
              }}
              className="font-monospace text-center"
              value={digit}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={length}
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              aria-label={digitAriaLabel(index + 1, length)}
              aria-describedby={fieldState.error ? errorId : undefined}
              aria-invalid={fieldState.error ? 'true' : undefined}
              isInvalid={Boolean(fieldState.error)}
              style={{
                width: 'clamp(2.25rem, 10vw, 3.25rem)',
                height: 'clamp(2.25rem, 10vw, 3.25rem)',
                ...(fieldState.error
                  ? {
                      backgroundImage: 'none',
                      paddingRight: '0.75rem'
                    }
                  : {})
              }}
              onChange={(event) => handleChange(index, event)}
              onPaste={(event) => handlePaste(index, event)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onBlur={field.onBlur}
              onFocus={(event) => event.currentTarget.select()}
            />
          </div>
        ))}
      </div>
      {fieldState.error && (
        <div id={errorId} className="invalid-feedback d-block" role="alert">
          {fieldState.error.message}
        </div>
      )}
    </Form.Group>
  );
}
